import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Eye, EyeOff, Lock, User, AlertCircle } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);

    try {
      // Real API call عبر AuthContext
      const user = await login(email, password);
      
      toast({
        title: 'Welcome back!',
        description: `Logged in as ${user.name}`,
        variant: "default",
        className: "bg-green-50 border-green-200 text-green-900"
      });
      
      navigate('/dashboard');
    } catch (err) {
      console.log('LOGIN ERROR:', err);
      setError(err.message || 'Invalid credentials');
      toast({
        title: 'Login Failed',
        description: err.message || 'Login error',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Stone Beam Projects</title>
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-navy-steal via-slate-800 to-safety-orange/20 animate-in fade-in duration-700">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center space-y-2 text-white">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-safety-orange/10 backdrop-blur-sm border border-safety-orange/20 mb-4">
              <Lock className="w-8 h-8 text-safety-orange" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Stone Beam</h1>
            <p className="text-slate-300">Project Management & HR Portal</p>
          </div>

          <Card className="bg-white/95 backdrop-blur-sm shadow-xl border-stone-200">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center text-navy-steal">Sign In</CardTitle>
              <CardDescription className="text-center">
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>

            <CardContent>
              {error && (
                <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 flex items-center gap-2 text-sm text-red-600 animate-in slide-in-from-top-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@stonebeam.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-safety-orange hover:text-safety-orange-hover font-medium"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9 pr-9"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-600 focus:outline-none"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-navy-steal hover:bg-navy-steal/90 h-11 text-base shadow-md transition-all hover:scale-[1.01] disabled:pointer-events-none disabled:opacity-50"
                  disabled={loading}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
            </CardContent>

            <CardFooter className="justify-center border-t border-stone-100 pt-6">
              <p className="text-xs text-stone-500 text-center">
                Protected by industry standard encryption. <br />
                © 2026 Stone Beam Demolition.
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
